import { AfterViewInit, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AufnahmePunktDocument } from '../../../data-model';
import { MapStatus, MapLimits, TransformationService, MapMarker, MapMarkerHandler, MAP_MARKER_HANDLER, LayerService, WmsDataService } from '@luzula/luzula-lib';
import { LatLng } from 'leaflet';
import { Subscription } from 'rxjs';
import { ValidationService } from '../../validation.service';
import { DataService } from '../../../data.service';
import { ProjectService } from '../../../project.service';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-map',
    templateUrl: './bohrpunkt-map.component.html',
    styleUrls: ['./bohrpunkt-map.component.css'],

})
export class BohrpunktMapComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
	private markerHandler = inject(MAP_MARKER_HANDLER) as MapMarkerHandler<AufnahmePunktDocument>;

	@Input() mapStatus!: MapStatus;
	@Input() mapLimits!: MapLimits;
  mapVisible = false;

  @Input() bp!: AufnahmePunktDocument;
  @Input() bps!: AufnahmePunktDocument[];
	@Input() pointsFilter!: boolean[];
	@Output() pointDeleted = new EventEmitter<void>();

	/** Konvertiert bps zu MapMarker[] für die MapComponent (gecacht, nur bei bps-Änderung neu berechnet) */
	mapMarkers: MapMarker[] = [];
	private cachedBps?: AufnahmePunktDocument[];

	private updateMapMarkers() {
		if (this.bps === this.cachedBps) return;
		this.cachedBps = this.bps;
		this.mapMarkers = this.bps
			?.filter(bp => this.markerHandler.hasValidCoordinates(bp))
			.map(bp => this.markerHandler.toMapMarker(bp)) ?? [];
	}

  point?: LatLng;

	// Koordinaten-Zurücksetzung bei Verwerfen
	private originalKoordinaten?: { x: number, y: number };
	private wasMoved = false;
	private pointSavedSub?: Subscription;

  ls = inject(LayerService);
  private ts = inject(TransformationService);
  private vs = inject(ValidationService);
  private wms_data = inject(WmsDataService);
  private ds = inject(DataService);
  private ps = inject(ProjectService);

	/**
	 * Punkt darf auf der Karte verschoben werden = derselbe Schreib-Check wie das
	 * Feld-Editieren (HlAreaDirective/Vegetation): `canIWritePoint` + dem aktiven
	 * Projekt zugeordnet. Sonst bleibt der Marker fix — sonst könnte ein
	 * read-only-Nutzer Koordinaten verschieben, die der WMS-Autosave persistiert.
	 */
	get canEdit(): boolean {
		return this.ps.canIWritePoint(this.bp)
			&& !!this.bp?.header?.projects?.some(p => p.id === this.ps.project());
	}

  ngOnInit(): void {
    this.mapVisible = true;
		// Nach explizitem Speichern: Originals aktualisieren
		this.pointSavedSub = this.ds.pointSaved.subscribe((savedBp: AufnahmePunktDocument) => {
			if (savedBp._id === this.bp?._id) {
				this.originalKoordinaten = { x: savedBp.header.koordinaten.x, y: savedBp.header.koordinaten.y };
				this.wasMoved = false;
			}
		});
  }

	private wmsDebounceTimer: any;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['bp']) {
			this.point = this.ts.from(this.bp.header.koordinaten);
			this.originalKoordinaten = { x: this.bp.header.koordinaten.x, y: this.bp.header.koordinaten.y };
			this.wasMoved = false;
		}
		if (changes['bps']) {
			this.updateMapMarkers();
		}
	}

	ngAfterViewInit(): void { }

  onPointChanged(latLng: LatLng) {
		// Defensiv: kein Reposition-Write an nicht schreibbaren Punkten (Befund 4).
		if (!this.canEdit) return;
		const pt = this.ts.toEPSG5650(latLng);
    this.point = latLng;
    this.bp.header.koordinaten.x = pt.x;
    this.bp.header.koordinaten.y = pt.y;
		this.wasMoved = true;
		this.vs.dirty.next(true);
		// Marker auf der Karte sofort aktualisieren (neues Objekt, damit SimpleChanges die Änderung erkennt)
		this.mapMarkers = this.mapMarkers.map(m =>
			m.id === this.bp._id ? { ...m, latLng } : m
		);
		this.cachedBps = undefined; // Cache invalidieren
		// WMS-Daten verzögert nachladen
		clearTimeout(this.wmsDebounceTimer);
		this.wmsDebounceTimer = setTimeout(() => {
			this.wms_data.fetchWmsDataForDocument(this.bp, true);
		}, 500);
  }

	ngOnDestroy(): void {
		clearTimeout(this.wmsDebounceTimer);
		this.pointSavedSub?.unsubscribe();
		// Koordinaten zurücksetzen wenn verschoben aber nicht gespeichert
		if (this.wasMoved && this.originalKoordinaten) {
			this.bp.header.koordinaten.x = this.originalKoordinaten.x;
			this.bp.header.koordinaten.y = this.originalKoordinaten.y;
			// Auch in DB zurücksetzen (WMS Auto-Save hat möglicherweise die verschobenen Koordinaten gespeichert)
			this.ds.saveDocumentQuiet(this.bp).catch(() => {});
		}
	}

	onPointDeleted() {
		this.pointDeleted.emit();
	}
}
